/**
 * PriceWithDiscount — precio de un plan en el modelo por MULTIPLICADOR:
 *   - número grande = equivalente MENSUAL del ciclo (eff.monthlyEquiv) + sufijo "/mes"
 *   - si hay descuento, el precio mensual de lista tachado al lado
 *   - sub-línea con el total del período (claves pricing.billed.*) en semestral/anual
 *   - badge "meses gratis" (1/2) para ciclos con freeMonths>0 (color --sun)
 *
 * El badge de % (DiscountBadge) queda SOLO para la promo de lanzamiento (aparte).
 *
 * Reutilizado por la landing (Plans/PlanCard) y el registro. Sólo tokens de color.
 * El cálculo del precio efectivo lo hace el llamador con effectivePrice(); acá
 * sólo pintamos.
 */
import type { Discounts, EffectivePrice, BillingCycle } from '../lib/pricing';
import { CYCLE_MONTHS } from '../lib/pricing';
import { t } from '../lib/i18n';

/** Formatea 'YYYY-MM-DD' (o datetime) a una fecha corta legible; fallback al crudo. */
function formatEndsAt(raw: string): string {
  const datePart = raw.slice(0, 10);
  const d = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Badge de la PROMO de lanzamiento (no del ciclo). Sólo se muestra si la promo
 * está activa; usa su etiqueta o "-{percent}%" y, si tiene, "termina el {fecha}".
 */
export function DiscountBadge({
  discounts,
  className = '',
}: {
  discounts?: Discounts;
  className?: string;
}) {
  const promo = discounts?.promo;
  if (!promo?.active) return null;

  const text = promo.label ? promo.label : t('discount.off', { pct: promo.percent });
  const endsAt = promo.ends_at;

  return (
    <span
      className={`inline-flex flex-wrap items-center gap-x-1.5 rounded-full bg-sun/15 px-2 py-0.5 text-xs font-medium text-sun ${className}`}
    >
      <span>{text}</span>
      {endsAt && (
        <span className="text-[10px] font-normal text-muted">
          {t('discount.endsAt', { date: formatEndsAt(endsAt) })}
        </span>
      )}
    </span>
  );
}

/** Badge "1 mes gratis" / "2 meses gratis" del ciclo (color --sun). */
function FreeMonthsBadge({ freeMonths }: { freeMonths: number }) {
  if (freeMonths <= 0) return null;
  const text = freeMonths >= 2 ? t('pricing.free.two') : t('pricing.free.one');
  return (
    <span className="inline-flex items-center rounded-full bg-sun/15 px-2 py-0.5 text-xs font-medium text-sun">
      {text}
    </span>
  );
}

/**
 * Precio con equivalente mensual + (si aplica) el total del período. `format`
 * convierte ₲ a la moneda elegida; `period` es el sufijo (normalmente "/mes").
 */
export function PriceWithDiscount({
  eff,
  cycle,
  format,
  period,
  priceClassName = 'font-display text-xl font-bold text-ink',
  periodClassName = 'text-sm text-muted',
}: {
  eff: EffectivePrice;
  cycle: BillingCycle;
  format: (pyg: number) => string;
  period?: string;
  priceClassName?: string;
  periodClassName?: string;
}) {
  const months = CYCLE_MONTHS[cycle];
  // Precio mensual de lista (tachado) = base del período / MESES = price_monthly.
  const listMonthly = eff.base / months;
  const billedKey =
    cycle === 'yearly'
      ? 'pricing.billed.yearly'
      : cycle === 'semiannual'
        ? 'pricing.billed.semiannual'
        : null;

  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex flex-wrap items-baseline gap-x-2">
        <span className={priceClassName}>{format(eff.monthlyEquiv)}</span>
        {eff.discounted && (
          <span className="text-sm text-muted line-through" aria-label={t('discount.wasPrice')}>
            {format(listMonthly)}
          </span>
        )}
        {period && <span className={periodClassName}>{period}</span>}
      </span>
      {eff.freeMonths > 0 && (
        <span className="inline-flex flex-wrap items-center gap-2">
          <FreeMonthsBadge freeMonths={eff.freeMonths} />
        </span>
      )}
      {billedKey && (
        <span className="text-xs text-muted">
          {t(billedKey, { total: format(eff.periodTotal) })}
        </span>
      )}
    </span>
  );
}
